/**
 * City visual states based on fatigue level AND time of day (MSK)
 * The city's appearance reflects both the user's inner state and actual time
 */

export type CityPhase = 'sleeping' | 'waking' | 'alive' | 'shining';
export type TimeOfDay = 'night' | 'morning' | 'day' | 'evening';

export interface CityState {
  phase: CityPhase;
  timeOfDay: TimeOfDay;
  starsOpacity: number;
  starsCount: number; // how many stars are visible (out of 15)
  lanternGlow: number;
  skyGradient: string;
  message: string;
  messageShort: string;
  accentOpacity: number;
  buildingVisibility: number;
  windowsLit: number; // percentage of lit windows 0-1
  auroraIntensity: number; // northern lights effect 0-1
}

/**
 * Get current Moscow time (UTC+3)
 */
function getMSKHour(): number {
  const now = new Date();
  const utcHour = now.getUTCHours();
  const mskHour = (utcHour + 3) % 24;
  return mskHour;
}

/**
 * Get time of day based on MSK hour
 * Night: 23:00 - 05:59
 * Morning: 06:00 - 11:59
 * Day: 12:00 - 17:59
 * Evening: 18:00 - 22:59
 */
export function getTimeOfDay(): TimeOfDay {
  const hour = getMSKHour();

  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'day';
  if (hour >= 18 && hour < 23) return 'evening';
  return 'night';
}

/**
 * Check if it's reflection time (after 21:00 MSK)
 */
export function isReflectionTime(): boolean {
  const hour = getMSKHour();
  return hour >= 21 || hour < 6; // 21:00-05:59 MSK
}

/**
 * Get MSK hour for display
 */
export function getMSKHourDisplay(): number {
  return getMSKHour();
}

/**
 * Sky gradients for different times of day
 * Each has variations for different fatigue levels (more muted when tired)
 */
const SKY_GRADIENTS: Record<TimeOfDay, Record<CityPhase, string>> = {
  night: {
    sleeping: 'linear-gradient(180deg, #050608 0%, #0A0C14 50%, #0D0E1A 100%)',
    waking: 'linear-gradient(180deg, #080A14 0%, #0D0F1A 50%, #151828 100%)',
    alive: 'linear-gradient(180deg, #0D0F1A 0%, #151828 50%, #1A1B35 100%)',
    shining: 'linear-gradient(180deg, #0D0F1A 0%, #1A1535 40%, #2D1B4E 100%)',
  },
  morning: {
    sleeping: 'linear-gradient(180deg, #1A1520 0%, #2D2535 50%, #352A40 100%)',
    waking: 'linear-gradient(180deg, #2D2535 0%, #453555 50%, #5A4565 100%)',
    alive: 'linear-gradient(180deg, #3D2F45 0%, #5A4565 40%, #7A5580 100%)',
    shining: 'linear-gradient(180deg, #4A3550 0%, #6A4570 30%, #9A6590 60%, #B87898 100%)',
  },
  day: {
    // Darker day gradients for better text readability
    sleeping: 'linear-gradient(180deg, #151830 0%, #1E2148 50%, #252858 100%)',
    waking: 'linear-gradient(180deg, #182040 0%, #253055 50%, #304068 100%)',
    alive: 'linear-gradient(180deg, #1A2545 0%, #2A3860 40%, #354A75 100%)',
    shining: 'linear-gradient(180deg, #1E2A50 0%, #304568 30%, #3D5580 60%, #4A6595 100%)',
  },
  evening: {
    sleeping: 'linear-gradient(180deg, #150A18 0%, #201525 50%, #2A1830 100%)',
    waking: 'linear-gradient(180deg, #201525 0%, #352040 50%, #4A2855 100%)',
    alive: 'linear-gradient(180deg, #2D1B3A 0%, #4A2855 40%, #652865 100%)',
    shining: 'linear-gradient(180deg, #351B45 0%, #552865 30%, #753878 60%, #954888 100%)',
  },
};

/**
 * Messages for different times and states
 */
const MESSAGES: Record<TimeOfDay, Record<CityPhase, { full: string; short: string }>> = {
  night: {
    sleeping: { full: 'Город спит. Фонари погасли.', short: 'Город спит...' },
    waking: { full: 'Ночь глубока, но кто-то не спит.', short: 'Глубокая ночь...' },
    alive: { full: 'Полуночники бродят по улицам.', short: 'Полуночники...' },
    shining: { full: 'Ночь полна чудес. Город сияет в темноте.', short: 'Ночь чудес!' },
  },
  morning: {
    sleeping: { full: 'Утро туманно. Город ещё дремлет.', short: 'Туманное утро...' },
    waking: { full: 'Первые лучи. Город просыпается.', short: 'Рассвет...' },
    alive: { full: 'Утренний свет наполняет улицы.', short: 'Утренний свет...' },
    shining: { full: 'Город сияет в утренних лучах!', short: 'Город сияет!' },
  },
  day: {
    sleeping: { full: 'День, но город устал и дремлет.', short: 'Дневная дрёма...' },
    waking: { full: 'День в разгаре. Город оживает.', short: 'День в разгаре...' },
    alive: { full: 'Улицы полны жизни и шёпотов.', short: 'Полны жизни...' },
    shining: { full: 'День прекрасен! Город пульсирует энергией.', short: 'Город пульсирует!' },
  },
  evening: {
    sleeping: { full: 'Вечер тяжёл. Город засыпает рано.', short: 'Ранний сон...' },
    waking: { full: 'Закат. В окнах зажигается свет.', short: 'Зажигаются огни...' },
    alive: { full: 'Вечерний час. Город наполняется тайнами.', short: 'Вечерние тайны...' },
    shining: { full: 'Волшебный вечер! Город сияет фонарями.', short: 'Волшебный вечер!' },
  },
};

/**
 * Get city visual state based on fatigue percentage AND time of day
 * Lower fatigue = brighter, more alive city
 * Time of day affects color palette
 */
export function getCityState(fatigue: number, maxFatigue: number = 100): CityState {
  const percentage = (fatigue / maxFatigue) * 100;
  const timeOfDay = getTimeOfDay();

  // Determine phase based on fatigue
  let phase: CityPhase;
  if (percentage >= 80) {
    phase = 'sleeping';
  } else if (percentage >= 50) {
    phase = 'waking';
  } else if (percentage >= 20) {
    phase = 'alive';
  } else {
    phase = 'shining';
  }

  const messages = MESSAGES[timeOfDay][phase];
  const skyGradient = SKY_GRADIENTS[timeOfDay][phase];

  // Stars visibility - more stars at night, fewer during day
  const timeStarsMultiplier = timeOfDay === 'night' ? 1.0 :
                               timeOfDay === 'evening' ? 0.8 :
                               timeOfDay === 'morning' ? 0.4 : 0.2;

  // Aurora more visible at night and evening
  const timeAuroraMultiplier = timeOfDay === 'night' ? 1.0 :
                                timeOfDay === 'evening' ? 0.7 : 0.3;

  // High fatigue (80-100%) - City sleeps
  if (percentage >= 80) {
    return {
      phase: 'sleeping',
      timeOfDay,
      starsOpacity: 0.2 * timeStarsMultiplier,
      starsCount: Math.floor(4 * timeStarsMultiplier),
      lanternGlow: 0,
      skyGradient,
      message: messages.full,
      messageShort: messages.short,
      accentOpacity: 0.4,
      buildingVisibility: 0.15,
      windowsLit: 0.05,
      auroraIntensity: 0,
    };
  }

  // Medium-high fatigue (50-79%) - City waking
  if (percentage >= 50) {
    return {
      phase: 'waking',
      timeOfDay,
      starsOpacity: 0.5 * timeStarsMultiplier,
      starsCount: Math.floor(8 * timeStarsMultiplier),
      lanternGlow: 0.3,
      skyGradient,
      message: messages.full,
      messageShort: messages.short,
      accentOpacity: 0.6,
      buildingVisibility: 0.4,
      windowsLit: 0.25,
      auroraIntensity: 0,
    };
  }

  // Medium-low fatigue (20-49%) - City alive
  if (percentage >= 20) {
    return {
      phase: 'alive',
      timeOfDay,
      starsOpacity: 0.8 * timeStarsMultiplier,
      starsCount: Math.floor(12 * timeStarsMultiplier),
      lanternGlow: 0.7,
      skyGradient,
      message: messages.full,
      messageShort: messages.short,
      accentOpacity: 0.85,
      buildingVisibility: 0.7,
      windowsLit: 0.6,
      auroraIntensity: 0.2 * timeAuroraMultiplier,
    };
  }

  // Low fatigue (0-19%) - City shining
  return {
    phase: 'shining',
    timeOfDay,
    starsOpacity: 1 * timeStarsMultiplier,
    starsCount: Math.floor(15 * timeStarsMultiplier),
    lanternGlow: 1,
    skyGradient,
    message: messages.full,
    messageShort: messages.short,
    accentOpacity: 1,
    buildingVisibility: 1,
    windowsLit: 0.9,
    auroraIntensity: 0.6 * timeAuroraMultiplier,
  };
}

/**
 * Apply city state to CSS variables
 */
export function applyCityState(state: CityState): void {
  const root = document.documentElement;
  root.style.setProperty('--stars-opacity', String(state.starsOpacity));
  root.style.setProperty('--lantern-glow', String(state.lanternGlow));
  root.style.setProperty('--sky-gradient', state.skyGradient);
  root.style.setProperty('--accent-opacity', String(state.accentOpacity));
  root.style.setProperty('--building-visibility', String(state.buildingVisibility));
  root.style.setProperty('--windows-lit', String(state.windowsLit));
  root.style.setProperty('--aurora-intensity', String(state.auroraIntensity));
}

/**
 * Get CSS class for city phase
 */
export function getCityPhaseClass(phase: CityPhase): string {
  return `city-${phase}`;
}
